import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Skeleton,
  Alert,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  VideoLibrary,
  Publish,
  Schedule,
  Language,
  RecordVoiceOver,
  TrendingUp,
  Visibility,
  ThumbUp,
  Share,
  Analytics,
  Add,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { projectService, reelService, statsService } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [project, setProject] = useState<any>(null);
  const [reels, setReels] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (id) {
      fetchProjectData();
    }
  }, [id]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const [projectRes, reelsRes, analyticsRes] = await Promise.all([
        projectService.get(Number(id)),
        reelService.list({ project_id: Number(id) }),
        statsService.project(Number(id), 30) // Last 30 days
      ]);
      
      setProject(projectRes.data);
      setReels(reelsRes.data || []);
      setAnalytics(analyticsRes.data || {});
    } catch (error) {
      console.error('Failed to fetch project data:', error);
      enqueueSnackbar('Failed to load project details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const languageIcons: Record<string, string> = {
    en: '🇺🇸',
    it: '🇮🇹',
    de: '🇩🇪',
    es: '🇪🇸',
    fr: '🇫🇷',
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3, borderRadius: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box>
        <Alert severity="error">
          Project not found
        </Alert>
      </Box>
    );
  }

  const progress = project.total_reels > 0 
    ? (project.published_reels / project.total_reels) * 100 
    : 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/projects')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {project.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={project.is_active ? 'Active' : 'Inactive'}
              color={project.is_active ? 'success' : 'default'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {languageIcons[project.language] || '🌐'} {project.language?.toUpperCase()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <RecordVoiceOver sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              {project.voice_type}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => navigate(`/projects/${id}/edit`)}
          sx={{ mr: 2 }}
        >
          Edit Project
        </Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/reels/create', { state: { projectId: id } })}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          Create Reel
        </Button>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ y: -4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <VideoLibrary sx={{ fontSize: 24, mr: 1, color: '#667eea' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total Reels
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {project.total_reels || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created in this project
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ y: -4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Publish sx={{ fontSize: 24, mr: 1, color: '#1dd1a1' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Published
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {project.published_reels || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {progress.toFixed(1)}% completion rate
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ y: -4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Visibility sx={{ fontSize: 24, mr: 1, color: '#54a0ff' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total Views
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {analytics?.total_views?.toLocaleString() || '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Across all platforms
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ y: -4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Schedule sx={{ fontSize: 24, mr: 1, color: '#feca57' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Avg. Render
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {Math.round(project.average_render_time || 0)}s
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Per reel generation
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Progress Bar */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Project Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {project.published_reels || 0} / {project.total_reels || 0} reels published
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 12,
              borderRadius: 6,
              bgcolor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 6,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {progress.toFixed(1)}% complete
          </Typography>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Reels" />
            <Tab label="Analytics" />
            <Tab label="Settings" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Reels List */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Project Reels ({reels.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/reels/create', { state: { projectId: id } })}
              size="small"
            >
              Create Reel
            </Button>
          </Box>

          {reels.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Views</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reels.map((reel) => (
                    <TableRow key={reel.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {reel.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {reel.description?.substring(0, 60)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={reel.status} 
                          color={getStatusColor(reel.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{reel.duration || 'N/A'}</TableCell>
                      <TableCell>
                        {new Date(reel.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{reel.total_views?.toLocaleString() || '0'}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => navigate(`/reels/${reel.id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <VideoLibrary sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No reels in this project yet
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/reels/create', { state: { projectId: id } })}
              >
                Create Your First Reel
              </Button>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Analytics */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Analytics Overview
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Engagement Metrics
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Total Views:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {analytics?.total_views?.toLocaleString() || '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Total Likes:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {analytics?.total_likes?.toLocaleString() || '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Total Shares:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {analytics?.total_shares?.toLocaleString() || '0'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Engagement Rate:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {analytics?.engagement_rate ? `${analytics.engagement_rate.toFixed(2)}%` : 'N/A'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Platform Performance
                  </Typography>
                  {analytics?.platform_stats && Array.isArray(analytics.platform_stats) ? 
                    analytics.platform_stats.map((platform: any) => (
                      <Box key={platform.platform} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {platform.platform}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {platform.total_views?.toLocaleString() || '0'} views
                        </Typography>
                      </Box>
                    )) : (
                      <Typography variant="body2" color="text.secondary">
                        No platform data available
                      </Typography>
                    )
                  }
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Project Settings */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Project Settings
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Basic Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1">{project.name}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{project.description || 'No description'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Language</Typography>
                    <Typography variant="body1">
                      {languageIcons[project.language] || '🌐'} {project.language?.toUpperCase()}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Voice Type</Typography>
                    <Typography variant="body1">{project.voice_type}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      label={project.is_active ? 'Active' : 'Inactive'}
                      color={project.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Statistics
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Created</Typography>
                    <Typography variant="body1">
                      {new Date(project.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                    <Typography variant="body1">
                      {new Date(project.updated_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Total Reels</Typography>
                    <Typography variant="body1">{project.total_reels || 0}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Published Reels</Typography>
                    <Typography variant="body1">{project.published_reels || 0}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => navigate(`/projects/${id}/edit`)}
            >
              Edit Project Settings
            </Button>
          </Box>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ProjectDetail;